import { IGameState } from "State/Game/IGameState";
import { IPlayerState, UnlockType } from "./IPlayerState";
import { isUnlocked, unlock } from "./PlayerUnlockHelper";
import Game from "Game";
import { LevelType } from "./Progress/PlayerLevels";
import { SEASON_PASS_PREMIUM_REMINDER, SEASON_PASS_UNLOCK_LEVEL } from "Constants/SeasonPass";
import { getSeasonPassEndDate, getSeasonPassSeed, getSeasonPassStartDate, isSeasonPassPremiumBought } from "Utility/SeasonPassHelper";
import { IPlayerSeasonPass, SeasonPassRank, SeasonPassReward, SeasonPassRewardType } from "Types/State/Player/SeasonPass";
import { NonVariableRewards } from "Types/Reward";
import DateHelper from "Utility/DateHelper";
import { hasFlag } from "./PlayerFlagHelper";
import { getRewardData, isRewardUnlocked, mergeRewards } from "Utility/RewardHelper";
import { SeasonPassTractorProgress } from "Types/SeasonPass";
import { ISeasonPassClaimRankRewardPayload } from "State/SeasonPass/SeasonPassActions";
import { InventoryStateHelper } from "./Inventory/InventoryStateHelper";
import { RewardType } from "Utility/Rewards/RewardType";
import { unlockLevelByPreBoosterType } from "System/UI/Menu/LevelPopup/LevelPopupBooster";
import { PreBoosterType } from "State/Card/BoosterType";
import { getHighestLevelCompleted } from "./PlayerLevelHelper";
import { FREE_LEVEL_ENTRY_COST } from "State/Card/BoosterDataMap";
import { trackResourceEvent } from "Utility/Tracking/TrackingHelpers/Transaction";
import { TransactionType, TActionType } from "Utility/Tracking/DataSet/Transaction";
import { getCurrentTractorPosition } from "System/UI/SeasonPass/TractorHelper";
import { AvoTracking } from "Utility/Tracking/Avo/AvoTracking";
import { PlayerStateHelper } from "./PlayerStateHelper";
import { PlayerTask } from "./PlayerTasks";
import { PopupID } from "State/UI/PopupID";

export function isSeasonPassUnlocked(state: IGameState): boolean {
    return false;
    if (isUnlocked(state, UnlockType.SEASON_PASS)) return true;
    const highestLevelCompleted = Game.player.getHighestLevelCompleted(state, LevelType.Core);
    if (highestLevelCompleted >= SEASON_PASS_UNLOCK_LEVEL) return true;
    return false;
}

export function checkSeasonPassUnlocked(state: IGameState): IGameState {
    const highestLevelCompleted = Game.player.getHighestLevelCompleted(state, LevelType.Core);
    if (isUnlocked(state, UnlockType.SEASON_PASS)) return state;
    if (highestLevelCompleted >= SEASON_PASS_UNLOCK_LEVEL) {
        state = unlock(state, { type: UnlockType.SEASON_PASS });
    }
    return state;
}

export function updateSeasonPass(state: IGameState): IGameState {
    if (state.playerState.seasonPass === null) {
        if (state.seasonPassData !== undefined) {
            state = setUpSeasonPass(state);
            return state;
        }
        return state;
    }
    if (hasSeasonPassEnded(state)) {
        if (hasUnclaimedSeasonPassReward(state)) state = collectUnclaimedSeasonPassEndRewards(state);
        state = setUpSeasonPass(state);
        return state;
    }
    if (hasPremiumReminder(state)) {
        const playerState = PlayerStateHelper.addStateTaskDatas(state.playerState, [
            {
                task: PlayerTask.SHOW_POPUP,
                prePlayTask: false,
                popup: PopupID.QUEST_MENU,
                meta: {
                    autoStatus: 'season_pass_reminder',
                },
                delay: 600,
            }
        ]);
        return {
            ...state,
            playerState,
        }
    }
    return state;
}

export function hasPremiumReminder(state: IGameState): boolean {
    if (!state.seasonPassData || !state.playerState.seasonPass) return false;
    const alreadyQueued = state.playerState.tasks.some(
        task => task.task === PlayerTask.SHOW_POPUP && task.popup === PopupID.QUEST_MENU
    );
    if (alreadyQueued) return false;
    return (
        state.platform.IAPSupport &&
        !isSeasonPassPremiumBought(state.playerState) &&
        !hasFlag(state.playerState, SEASON_PASS_PREMIUM_REMINDER) &&
        (hasEndReminder(state) || hasMilestoneReminder(state))
    );
}

export function hasEndReminder(state: IGameState): boolean {
    return (
        state.playerState.seasonPass.currentRank >= state.seasonPassData.premiumReminder &&
        getSeasonPassEndDate().getTime() - DateHelper.now.getTime() <
        DateHelper.daysToMilliseconds(5)
    );
}

export function hasMilestoneReminder(state: IGameState): boolean {
    if (
        hasFlag(
            state.playerState,
            getSeasonPassSeed() + SEASON_PASS_PREMIUM_REMINDER + state.playerState.seasonPass.currentRank
        )
    )
        return false;
    return !!state.seasonPassData.ranks[state.playerState.seasonPass.currentRank - 1]?.isMilestone;
}

function hasSeasonPassEnded(state: IGameState): boolean {
    if (state.playerState.seasonPass?.seasonPassID !== getSeasonPassSeed()) return true;
    return DateHelper.now > DateHelper.getDateFromValue(state.playerState.seasonPass.endDate);
}

function setUpSeasonPass(state: IGameState, unlockPremium = false): IGameState {
    if (!state.seasonPassData) return state;
    const seasonPassData: IPlayerSeasonPass = {
        boughtPremiumPass: unlockPremium,
        progress: 0,
        currentRank: 0,
        endDate: getSeasonPassEndDate().toString(),
        ranks: generateSeasonPassRewards(state),
        seasonPassID: getSeasonPassSeed(),
        startDate: getSeasonPassStartDate().toString(),
        endRewards: undefined,
        lastSeenProgress: undefined,
    };
    state = {
        ...state,
        playerState: {
            ...state.playerState,
            seasonPass: seasonPassData,
        },
    };
    state = {
        ...state,
        playerState: updateSeasonPassProgress(state.playerState)
    };
    return state;
}

function generateSeasonPassRewards(state: IGameState): SeasonPassRank[] {
    const ranks: SeasonPassRank[] = [];
    state.seasonPassData.ranks.forEach((rank, index) => {
        ranks.push({
            free: {
                rewards: convertToFallbackRewards(state, rank.freeRewards),
                state: 'locked',
            },
            premium: {
                rewards: convertToFallbackRewards(state, rank.premiumRewards),
                state: 'locked',
            },
            neededProgress: rank.progress,
        });
    });
    return ranks;
}

function convertToFallbackRewards(state: IGameState, rewards: NonVariableRewards): NonVariableRewards {
    return rewards.map(reward => {
        switch (reward.type) {
            case RewardType.BULLSEYE:
            case RewardType.BOMB:
            case RewardType.SHUFFLE:
                if (!isRewardUnlocked(state.playerState, reward.type))
                    reward = {
                        type: RewardType.COINS,
                        amount: getRewardData(reward.type).fallbackLevelEntry * reward.amount * FREE_LEVEL_ENTRY_COST,
                    };
                break;
            case RewardType.PRE_BOOSTER_CANISTER:
            case RewardType.PRE_BOOSTER_JOKER:
            case RewardType.PRE_BOOSTER_REMOVE_THREE:
                const rewardType = (reward.type === RewardType.PRE_BOOSTER_JOKER) ? PreBoosterType.JOKER : (reward.type === RewardType.PRE_BOOSTER_REMOVE_THREE) ? PreBoosterType.REMOVE_THREE : PreBoosterType.CANISTER;
                if (!isRewardUnlocked(state.playerState, reward.type) || getHighestLevelCompleted(state.playerState) + 1 < unlockLevelByPreBoosterType[rewardType])
                    reward = {
                        type: RewardType.COINS,
                        amount: getRewardData(reward.type).fallbackLevelEntry * reward.amount * FREE_LEVEL_ENTRY_COST,
                    };
                break;
        }
        return reward;
    });
}

export function updateSeasonPassProgress(playerState: IPlayerState): IPlayerState {
    let previouslyFinishedSeasonPass = finishedSeasonPass(playerState);
    while (isSeasonPassRankUp(playerState)) {
        playerState = increaseSeasonPassRank(playerState);

        if (!previouslyFinishedSeasonPass && finishedSeasonPass(playerState)) {
        }

        playerState = setSeasonPassRewardClaimable(playerState, playerState.seasonPass.currentRank, 'free');
        if (isSeasonPassPremiumBought(playerState))
            playerState = setSeasonPassRewardClaimable(playerState, playerState.seasonPass.currentRank, 'premium');
    }
    return playerState;
}

function isSeasonPassRankUp(playerState: IPlayerState): boolean {
    const seasonPass = playerState.seasonPass;
    if (!seasonPass || finishedSeasonPass(playerState)) return false;
    return seasonPass.progress >= playerState.seasonPass.ranks[seasonPass.currentRank].neededProgress;
}

export function finishedSeasonPass(playerState: IPlayerState, lastSeen = false): boolean {
    if (lastSeen) {
        return playerState.seasonPass.lastSeenProgress?.rank >= playerState.seasonPass?.ranks.length;
    }
    return playerState.seasonPass?.currentRank >= playerState.seasonPass?.ranks.length;
}

export function increaseSeasonPassProgress(playerState: IPlayerState, value: number): IPlayerState {
    if (!playerState.seasonPass) return playerState;
    playerState = {
        ...playerState,
        seasonPass: {
            ...playerState.seasonPass,
            progress: playerState.seasonPass.progress + value,
        },
    };
    return updateSeasonPassProgress(playerState);
}

export function setSeasonPassProgress(playerState: IPlayerState, value: number): IPlayerState {
    if (!playerState.seasonPass) return playerState;
    playerState = {
        ...playerState,
        seasonPass: {
            ...playerState.seasonPass,
            progress: value,
        },
    };
    return updateSeasonPassProgress(playerState);
}

export function setSeasonPassLastSeenProgress(playerState: IPlayerState, value: SeasonPassTractorProgress): IPlayerState {
    if (!playerState.seasonPass) return playerState;
    playerState = {
        ...playerState,
        seasonPass: {
            ...playerState.seasonPass,
            lastSeenProgress: value,
        },
    };
    return updateSeasonPassProgress(playerState);
}

export function isSeasonPassRewardSeen(playerState: IPlayerState, rank: number): boolean {
    return getSeasonPassLastSeenProgress(playerState).rank >= rank;
}

/** Which progress of the season pass the player has seen last time. */
export function getSeasonPassLastSeenProgress(playerState: IPlayerState): SeasonPassTractorProgress {
    if (!playerState.seasonPass?.lastSeenProgress) return { rank: 1, progress: 0 };
    return playerState.seasonPass.lastSeenProgress;
}

function increaseSeasonPassRank(playerState: IPlayerState): IPlayerState {
    const newProgress =
        playerState.seasonPass.progress -
        playerState.seasonPass.ranks[playerState.seasonPass.currentRank].neededProgress;
    playerState = {
        ...playerState,
        seasonPass: {
            ...playerState.seasonPass,
            progress: newProgress,
            currentRank: playerState.seasonPass.currentRank + 1,
        },
    };
    return playerState;
}

function setSeasonPassRewardClaimable(playerState: IPlayerState, rank: number, type: SeasonPassRewardType): IPlayerState {
    const ranks = playerState.seasonPass.ranks;
    const newRank = playerState.seasonPass.ranks[rank - 1];
    if (newRank[type].state === 'locked') newRank[type].state = 'claimable';
    ranks[rank - 1] = newRank;
    return {
        ...playerState,
        seasonPass: {
            ...playerState.seasonPass,
            ranks,
        },
    };
}

export function hasUnclaimedSeasonPassReward(state: IGameState): boolean {
    return (
        state.playerState.seasonPass?.ranks.some(reward => {
            return reward.free.state === 'claimable' || reward.premium.state === 'claimable';
        })
    );
}

function collectUnclaimedSeasonPassEndRewards(state: IGameState): IGameState {
    if (state.playerState.seasonPass.endRewards !== undefined) return state;
    let rewards: NonVariableRewards = [];
    state.playerState.seasonPass.ranks.forEach(rank => {
        if (rank.free.state === 'claimable') rewards = [...rewards, ...rank.free.rewards];
        if (rank.premium.state === 'claimable') rewards = [...rewards, ...rank.premium.rewards];
    });
    rewards = mergeRewards(rewards);
    state = {
        ...state,
        playerState: {
            ...state.playerState,
            seasonPass: {
                ...state.playerState.seasonPass,
                endRewards: rewards,
            },
        },
    };
    return state;
}

export function getSeasonPassRewardForRank(
    state: IGameState,
    rank: number,
    type: SeasonPassRewardType
): SeasonPassReward {
    return state.playerState.seasonPass.ranks[rank - 1][type];
}

export function claimSeasonPassReward(state: IGameState, rank: number, type: SeasonPassRewardType): IGameState {
    const ranks = state.playerState.seasonPass.ranks;
    const newRank = state.playerState.seasonPass.ranks[rank - 1];
    if (newRank[type].state === 'claimable') {
        newRank[type].state = 'claimed';
        const rewards = newRank[type].rewards.map(reward => {
            return {
                type: reward.type,
                value: reward.amount
            }
        });
        const firstRankReward = getCurrentTractorPosition(state.playerState).rank === 1 && getCurrentTractorPosition(state.playerState).progress === 0;
        const newPlayerState = InventoryStateHelper.receiveRewards(state.playerState, rewards, { queueTask: true, queueNext: !firstRankReward });
        state = {
            ...state,
            playerState: newPlayerState,
        };
    }
    ranks[rank - 1] = newRank;
    trackResourceEvent(state, {
        transactionType: TransactionType.REWARD,
        tActionType: TActionType.SEASONPASS,
    });
    return {
        ...state,
        playerState: {
            ...state.playerState,
            seasonPass: {
                ...state.playerState.seasonPass,
                ranks,
            },
        },
    };
}

export function claimRankReward(state: IGameState, payload: ISeasonPassClaimRankRewardPayload): IGameState {
    return claimSeasonPassReward(state, payload.rank, payload.type);
}

export function resetSeasonPass(state: IGameState, unlockPremium = false): IGameState {
    state = fullFillUnclaimedSeasonPassRewards(state);
    state = {
        ...state,
        playerState: {
            ...state.playerState,
            seasonPass: null,
        },
    };
    state = setUpSeasonPass(state);
    return state;
}

function fullFillUnclaimedSeasonPassRewards(state: IGameState): IGameState {
    if (state.playerState.seasonPass?.endRewards === undefined || state.playerState.seasonPass?.endRewards.length === 0)
        return state;
    const rewards = state.playerState.seasonPass.endRewards.map(reward => {
        return {
            type: reward.type,
            value: reward.amount
        }
    });
    const newPlayerState = InventoryStateHelper.receiveRewards(state.playerState, rewards, { queueTask: true, queueNext: true });
    state = {
        ...state,
        playerState: newPlayerState,
    };
    trackResourceEvent(state, {
        transactionType: TransactionType.REWARD,
        tActionType: TActionType.SEASONPASS,
    });
    return state;
}

export function unlockPremiumPass(playerState: IPlayerState): IPlayerState {
    for (let i = 1; i <= playerState.seasonPass.currentRank; i++) {
        setSeasonPassRewardClaimable(playerState, i, 'premium');
    }


    return {
        ...playerState,
        seasonPass: {
            ...playerState.seasonPass,
            boughtPremiumPass: true,
        },
    };
}

export function getLastSeenSeasonPassProgressDifference(playerState: IPlayerState): number {
    const lastSeenProgress = getSeasonPassLastSeenProgress(playerState);
    const currentProgress = getCurrentTractorPosition(playerState);
    if (lastSeenProgress.rank !== currentProgress.rank || lastSeenProgress.progress !== currentProgress.progress) {
        let progressDifference = 0;
        if (lastSeenProgress.rank === currentProgress.rank) {
            progressDifference += currentProgress.progress - lastSeenProgress.progress;
        } else {
            if (lastSeenProgress.rank !== currentProgress.rank) {
                progressDifference += playerState.seasonPass.ranks[lastSeenProgress.rank].neededProgress - lastSeenProgress.progress;
            }
            for (let i = lastSeenProgress.rank + 1; i < currentProgress.rank; i++) {
                progressDifference += playerState.seasonPass.ranks[i].neededProgress;
            }
            progressDifference += currentProgress.progress;
        }
        return progressDifference;
    } else {
        return 0;
    }
}

export function getTotalSeasonPassPoints(playerState: IPlayerState): number {
    if (!playerState.seasonPass) return 0;
    let progress = playerState.seasonPass.progress;
    for (let i = 0; i < playerState.seasonPass.currentRank; i++) {
        progress += playerState.seasonPass.ranks[i].neededProgress;
    }
    return progress;
}